import React, { memo } from 'react';
import { Badge, Button, Col, Modal, Row } from 'react-bootstrap';

import { Feedback } from '../../../types';
import StarRating from '../StarRating';
import './FeedbackDetailsModal.css';

interface FeedbackDetailsModalProps {
  show: boolean;
  feedback: Feedback | null;
  onClose: () => void;
}

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

const ratingVariant = (rating: number): string => {
  if (rating <= 2) return 'danger';
  if (rating === 3) return 'warning';
  return 'success';
};

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = memo(({ label, children }) => (
  <div className="feedback-details-modal__field">
    <div className="feedback-details-modal__field-label">{label}</div>
    <div className="feedback-details-modal__field-value">{children}</div>
  </div>
));
Field.displayName = 'FeedbackDetailsField';

/**
 * Modal de detalhe completo de um feedback (visão administrativa).
 *
 * - Responsivo (grid Bootstrap, breakpoints em CSS).
 * - Acessível (role dialog do Modal, aria-labelledby, closeButton, Esc fecha).
 * - Padronizado com o resto do projeto (mesmo header primary-blue, ícones bi).
 * - Seguro: React escapa HTML automaticamente em todos os campos exibidos.
 *   Para o comentário usamos <div>{texto}</div> (text node) e nunca
 *   dangerouslySetInnerHTML, blindando contra XSS no nível do componente.
 * - Sem memory leak: componente totalmente declarativo, sem timers/listeners.
 * - Performance: React.memo + sub-componente Field memoizado para evitar
 *   renders desnecessários quando o feedback não muda.
 * - Anonimato: a coluna "Anonimato" indica se o envio foi anônimo; nome e e-mail
 *   do autor são exibidos aqui (tela só acessível a admin; backend envia user).
 */
const FeedbackDetailsModal: React.FC<FeedbackDetailsModalProps> = memo(({
  show,
  feedback,
  onClose,
}) => {
  // Renderiza modal mesmo sem feedback (mantém transição de fade do Bootstrap),
  // mas o conteúdo só aparece quando há dados. Evita "flash" e garante que
  // closeButton/Esc continuem funcionando durante o fechamento.
  return (
    <Modal
      show={show && Boolean(feedback)}
      onHide={onClose}
      centered
      scrollable
      size="lg"
      aria-labelledby="feedback-details-title"
      className="feedback-details-modal"
    >
      <Modal.Header closeButton className="feedback-details-modal__header">
        <Modal.Title id="feedback-details-title" className="feedback-details-modal__title">
          <i className="bi bi-clipboard-data me-2" aria-hidden="true"></i>
          Detalhes do feedback {feedback ? `#${feedback.id}` : ''}
        </Modal.Title>
      </Modal.Header>

      {feedback && (
        <Modal.Body className="feedback-details-modal__body">
          <Row className="g-3">
            <Col xs={12} sm={6}>
              <Field label="Usuário">
                {feedback.user?.name ?? '—'}
                {feedback.is_anonymous && (
                  <div className="text-muted small mt-1">
                    Enviado como anônimo (identidade visível apenas para administração)
                  </div>
                )}
              </Field>
            </Col>

            <Col xs={12} sm={6}>
              <Field label="E-mail">
                {feedback.user?.email ?? '—'}
              </Field>
            </Col>

            <Col xs={12} sm={6}>
              <Field label="Escopo avaliado">
                {feedback.display_label ? (
                  <span>{feedback.display_label}</span>
                ) : feedback.workflow ? (
                  <span>
                    {feedback.workflow.name}{' '}
                    <span className="text-muted">(v{feedback.workflow.version})</span>
                  </span>
                ) : (
                  <em className="text-muted">Plataforma (geral)</em>
                )}
              </Field>
            </Col>

            <Col xs={12} sm={6}>
              <Field label="Anonimato">
                {feedback.is_anonymous ? (
                  <Badge bg="secondary">
                    <i className="bi bi-incognito me-1" aria-hidden="true"></i>
                    Sim
                  </Badge>
                ) : (
                  <Badge bg="light" text="dark">Não</Badge>
                )}
              </Field>
            </Col>

            <Col xs={12} sm={6}>
              <Field label="Avaliação">
                <div className="feedback-details-modal__rating">
                  <StarRating
                    value={feedback.rating}
                    onChange={() => undefined}
                    readOnly
                    size="sm"
                    ariaLabel={`Avaliação: ${feedback.rating} de 5 estrelas`}
                  />
                  <Badge bg={ratingVariant(feedback.rating)}>
                    {feedback.rating} / 5
                  </Badge>
                </div>
              </Field>
            </Col>

            <Col xs={12} sm={6}>
              <Field label="Identificador">
                <code className="feedback-details-modal__id">#{feedback.id}</code>
              </Field>
            </Col>

            <Col xs={12} sm={6}>
              <Field label="Criado em">{formatDateTime(feedback.created_at)}</Field>
            </Col>

            <Col xs={12} sm={6}>
              <Field label="Atualizado em">{formatDateTime(feedback.updated_at)}</Field>
            </Col>

            <Col xs={12}>
              <Field label="Comentário">
                {feedback.comment ? (
                  <div className="feedback-details-modal__comment">{feedback.comment}</div>
                ) : (
                  <em className="text-muted">Sem comentário</em>
                )}
              </Field>
            </Col>
          </Row>
        </Modal.Body>
      )}

      <Modal.Footer className="feedback-details-modal__footer">
        <Button variant="secondary" onClick={onClose}>
          <i className="bi bi-x-lg me-1" aria-hidden="true"></i>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

FeedbackDetailsModal.displayName = 'FeedbackDetailsModal';

export default FeedbackDetailsModal;
